/** Remitter KYC routing: AD-II new, AD-II re-KYC, legacy document upload, or none. */
export type RemitterKycFlowType = "none" | "ad_ii_new" | "ad_ii_re" | "legacy";

export type RemitterKycFlowDecision = {
  flow: RemitterKycFlowType;
  shouldOpenSendLinkModal: boolean;
};

export const isAdIIPurpose = (adCategory?: string | null): boolean => adCategory === "AD-II";

export const isAdIIKycFlow = (flow: RemitterKycFlowType): boolean =>
  flow === "ad_ii_new" || flow === "ad_ii_re";

export const isLegacyKycFlow = (flow: RemitterKycFlowType): boolean => flow === "legacy";

export const isAdIIKycProfileComplete = (profile?: any): boolean => {
  const profileStatus = String(profile?.profileStatus ?? "").toLowerCase();
  const reviewerAction = String(
    profile?.profileReviewerAction ?? profile?.profileReviewerStatus ?? ""
  ).toLowerCase();
  return profileStatus === "completed" && reviewerAction === "approved";
};

/** AD-II KYC complete — checks nested profile and remitter-level fields from list/details API. */
export const isAdIIKycComplete = (remitterDetails?: any): boolean => {
  if (!remitterDetails) return false;

  if (isAdIIKycProfileComplete(remitterDetails.adIIKycProfileDetails)) {
    return true;
  }

  const profileStatus = String(remitterDetails.profileStatus ?? "").toLowerCase();
  const reviewerStatus = String(
    remitterDetails.profileReviewerStatus ?? remitterDetails.profileReviewerAction ?? ""
  ).toLowerCase();

  return profileStatus === "completed" && reviewerStatus === "approved";
};

/** True when remitter should use AD-II send-link KYC (not legacy resend-link). */
export const isAdIIKycRequired = (
  remitterDetails?: any,
  adCategory?: string | null
): boolean => {
  if (!remitterDetails || !isAdIIPurpose(adCategory) || remitterDetails.isDocVerified) {
    return false;
  }

  if (!getTransactionVendorNewKycEnabled(remitterDetails)) {
    return false;
  }

  if (isAdIIKycComplete(remitterDetails)) {
    return false;
  }

  return (
    Boolean(remitterDetails.adIINewKycRequired) ||
    Boolean(remitterDetails.adIIReKycRequired) ||
    Boolean(remitterDetails?.transactionVendor?.adIINewKycRequired)
  );
};

export const shouldShowLegacyKycShareLink = (
  remitterDetails?: any,
  adCategory?: string | null
): boolean => {
  if (!remitterDetails || remitterDetails.isDocVerified) return false;

  // AD-II + transaction vendor new KYC → hide old Send/Copy share link.
  if (isAdIIPurpose(adCategory) && getTransactionVendorNewKycEnabled(remitterDetails)) {
    return false;
  }

  return true;
};

export type AdIIListKycStatusTone = "Verified" | "Failed" | "In Progress" | "Link Sent";

export type AdIIListKycStatus = {
  label: string;
  tone: AdIIListKycStatusTone;
};

/** Display-only: completed + reviewer rejected → show as rejected (does not mutate API fields). */
export const isAdIIKycRejected = (source?: any): boolean => {
  if (!source) return false;

  const profile = source?.adIIKycProfileDetails ?? source;
  const profileStatus = String(
    profile?.profileStatus ?? source?.profileStatus ?? ""
  ).toLowerCase();

  const reviewerValues = [
    profile?.profileReviewerStatus,
    profile?.profileReviewerAction,
    source?.profileReviewerStatus,
    source?.profileReviewerAction,
  ]
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
    .map((value) => String(value).toLowerCase());

  return profileStatus === "completed" && reviewerValues.includes("rejected");
};

const AD_II_IN_PROGRESS_STATUSES = ["in_progress", "review_required", "processed"];

export const getAdIIProfileStatus = (source?: any) =>
  String(source?.profileStatus ?? "").toLowerCase();

export const getAdIIReviewerAction = (source?: any) =>
  String(source?.profileReviewerAction ?? source?.profileReviewerStatus ?? "").toLowerCase();

export const isAdIIKycInProgressStatus = (source?: any) =>
  AD_II_IN_PROGRESS_STATUSES.includes(getAdIIProfileStatus(source));

export const formatAdIIProfileStatusLabel = (source?: any) => {
  const raw = String(source?.profileStatus || "").trim();
  if (!raw) return "—";
  return raw.replace(/_/g, " ").toUpperCase();
};

/** Generate new link: cancelled, completed+rejected, or canGenerateNewLink — never for in-progress statuses. */
export const shouldShowAdIIGenerateNewLink = (source?: any) => {
  if (!source || isAdIIKycInProgressStatus(source)) return false;
  if (getAdIIProfileStatus(source) === "capture_pending") return false;
  if (isAdIIKycRejected(source)) return true;
  if (getAdIIProfileStatus(source) === "cancelled") return true;
  return Boolean(source.canGenerateNewLink);
};

export type AdIIKycLinkScenario =
  | "not_sent"
  | "link_sent"
  | "generate_new"
  | "rejected"
  | "in_progress"
  | "approved";

export const getAdIIKycLinkScenario = (
  profileDetails?: any,
  isLinkSentLocally?: boolean
): AdIIKycLinkScenario => {
  const status = getAdIIProfileStatus(profileDetails);
  const reviewer = getAdIIReviewerAction(profileDetails);

  if (status === "completed" && reviewer === "approved") return "approved";
  if (isAdIIKycRejected(profileDetails)) return "rejected";
  if (status === "capture_pending") return "link_sent";
  if (isAdIIKycInProgressStatus(profileDetails)) return "in_progress";
  if (Boolean(profileDetails?.canGenerateNewLink) || status === "cancelled") return "generate_new";

  const hasActiveLink =
    Boolean(isLinkSentLocally) ||
    Boolean(profileDetails?.canResend) ||
    Boolean(profileDetails?.canCopyLink) ||
    String(profileDetails?.status || "").toUpperCase() === "LINK_SENT";

  if (hasActiveLink) return "link_sent";
  return "not_sent";
};

/** List-column label + KYC Status badge tone for AD-II KYC Status. */
export const getAdIIListKycStatus = (remitter?: any): AdIIListKycStatus => {
  if (remitter?.adIIReKycRequired) {
    return { label: "RE-KYC", tone: "Failed" };
  }

  const profileStatus = remitter?.profileStatus;
  const profileReviewerAction =
    remitter?.profileReviewerAction ?? remitter?.profileReviewerStatus;
  const isEmpty = (value: unknown) =>
    value === null || value === undefined || String(value).trim() === "";

  if (isEmpty(profileStatus) && isEmpty(profileReviewerAction)) {
    return { label: "NOT STARTED", tone: "Link Sent" };
  }

  // Display-only mapping: completed + rejected → REJECTED (original fields unchanged).
  if (isAdIIKycRejected(remitter)) {
    return { label: "REJECTED", tone: "Failed" };
  }

  const statusText = String(profileStatus || "").trim();
  if (!statusText) {
    return { label: "—", tone: "In Progress" };
  }

  const label = statusText.toUpperCase();
  const statusKey = statusText.toLowerCase();
  if (statusKey === "completed") return { label, tone: "Verified" };
  if (statusKey === "cancelled") return { label, tone: "Failed" };
  return { label, tone: "In Progress" };
};

/** History AD-II KYC status label (display-only). */
export const getAdIIHistoryKycStatusLabel = (vkyc?: any): string => {
  if (!vkyc) return "—";

  const reviewerValues = [
    vkyc.profileReviewerAction,
    vkyc.profileReviewerStatus,
  ]
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== "")
    .map((value) => String(value).toLowerCase());

  if (reviewerValues.includes("rejected")) {
    return "REJECTED";
  }

  const profileStatus = String(vkyc.profileStatus || "").trim();
  if (profileStatus) {
    return profileStatus.toUpperCase();
  }

  const status = String(vkyc.status || "").trim();
  return status ? status.toUpperCase() : "—";
};

/** Gate for new vs old KYC — only `transactionVendor.adIINewKycRequired`. */
export const getTransactionVendorNewKycEnabled = (
  remitterDetails?: any,
  transactionVendorAdIINewKycRequired?: boolean | null
): boolean => {
  if (typeof transactionVendorAdIINewKycRequired === "boolean") {
    return transactionVendorAdIINewKycRequired;
  }
  return Boolean(remitterDetails?.transactionVendor?.adIINewKycRequired);
};

/**
 * AD-II only: transactionVendor.adIINewKycRequired → new KYC, else old KYC.
 * AD-I / other purposes: always old KYC flow (unchanged).
 */
export const resolveRemitterKycFlow = ({
  remitterDetails,
  adCategory,
  transactionVendorAdIINewKycRequired,
}: {
  remitterDetails?: any;
  adCategory?: string | null;
  transactionVendorAdIINewKycRequired?: boolean | null;
}): RemitterKycFlowDecision => {
  if (!remitterDetails) {
    return { flow: "none", shouldOpenSendLinkModal: false };
  }

  const isAdII = isAdIIPurpose(adCategory);

  // AD-I (and non-AD-II): keep existing old document KYC flow.
  if (!isAdII) {
    if (!remitterDetails.isDocVerified) {
      return { flow: "legacy", shouldOpenSendLinkModal: false };
    }
    return { flow: "none", shouldOpenSendLinkModal: false };
  }

  // AD-II: switch new/old KYC only by transactionVendor.adIINewKycRequired.
  const isTxnVendorNewKycEnabled = getTransactionVendorNewKycEnabled(
    remitterDetails,
    transactionVendorAdIINewKycRequired
  );

  if (isTxnVendorNewKycEnabled) {
    const isReKycRequired = Boolean(remitterDetails.adIIReKycRequired);

    if (isAdIIKycComplete(remitterDetails) && !isReKycRequired) {
      return { flow: "none", shouldOpenSendLinkModal: false };
    }

    return {
      flow: isReKycRequired ? "ad_ii_re" : "ad_ii_new",
      shouldOpenSendLinkModal: true,
    };
  }

  // AD-II + transactionVendor.adIINewKycRequired = false → old KYC.
  if (!remitterDetails.isDocVerified) {
    return { flow: "legacy", shouldOpenSendLinkModal: false };
  }

  return { flow: "none", shouldOpenSendLinkModal: false };
};

export const getAdIIApiError = (err: any, fallback = "Something went wrong"): string => {
  const errorMsg =
    err?.response?.data?.error?.type ||
    err?.response?.data?.error ||
    err?.response?.error?.type ||
    err?.response?.error ||
    err?.message ||
    fallback;

  return typeof errorMsg === "string" ? errorMsg : fallback;
};

export const getAdIIKycShareLink = (profileDetails?: any): string =>
  profileDetails?.portalLink || "";

/** Seed nested AD-II profile from remitter list/details fields (status stays display-only source of truth). */
export const buildAdIIKycProfileDetailsFromRemitter = (remitterDetails?: any) => {
  if (!remitterDetails) return null;

  const existing = remitterDetails.adIIKycProfileDetails || {};

  return {
    ...existing,
    profileStatus: existing.profileStatus ?? remitterDetails.profileStatus,
    profileReviewerAction:
      existing.profileReviewerAction ??
      remitterDetails.profileReviewerAction ??
      remitterDetails.profileReviewerStatus,
    profileReviewerStatus:
      existing.profileReviewerStatus ??
      remitterDetails.profileReviewerStatus ??
      remitterDetails.profileReviewerAction,
    uniqueKey:
      existing.uniqueKey ?? remitterDetails.uniqueKey ?? remitterDetails.vkycUniqueKey,
    status: existing.status ?? remitterDetails.status ?? remitterDetails.vkycStatus,
    captureLink: existing.captureLink ?? remitterDetails.captureLink,
    portalLink: existing.portalLink ?? remitterDetails.portalLink,
    captureExpiresAt: existing.captureExpiresAt ?? remitterDetails.captureExpiresAt,
    phone: existing.phone ?? remitterDetails.phoneNumber ?? remitterDetails.phone,
    email: existing.email ?? remitterDetails.email,
    canGenerateNewLink:
      existing.canGenerateNewLink ?? remitterDetails.canGenerateNewLink,
    canResend: existing.canResend ?? remitterDetails.canResend,
    canCopyLink: existing.canCopyLink ?? remitterDetails.canCopyLink,
    expired: existing.expired ?? remitterDetails.expired,
    completed: existing.completed ?? remitterDetails.completed,
  };
};

export const mergeAdIIKycProfileDetails = (current: any, profileData: any) => {
  if (!profileData) return current;

  const nestedProfile = profileData?.profileDetails || {};

  return {
    ...current,
    ...nestedProfile,
    ...profileData,
    uniqueKey: profileData?.uniqueKey ?? current?.uniqueKey,
    status: profileData?.status ?? current?.status,
    captureExpiresAt:
      profileData?.captureExpiresAt ?? nestedProfile?.captureExpiresAt ?? current?.captureExpiresAt,
    portalLink: profileData?.portalLink ?? current?.portalLink,
    captureLink: profileData?.captureLink ?? current?.captureLink,
    phone: profileData?.phone ?? current?.phone,
    email: profileData?.email ?? current?.email,
  };
};
